import axios from './axios'

interface LoginResponse {
  token?: string
  user?: {
    id: number | string
    username: string
    company: string
  }
  error?: string
}

interface UserSession {
  id: string | number
  username: string
  company: string
}

class AuthService {
  private tokenCheckInterval: number | null = null

  constructor() {
    // Iniciar verificación periódica del token si existe
    if (this.getToken()) {
      this.startTokenVerification()
    }
  }

  private startTokenVerification() {
    // Verificar token cada 5 minutos
    this.tokenCheckInterval = window.setInterval(
      () => {
        this.verifyToken()
      },
      5 * 60 * 1000,
    )
  }

  private stopTokenVerification() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval)
      this.tokenCheckInterval = null
    }
  }

  private async verifyToken(): Promise<boolean> {
    const token = this.getToken()
    if (!token) return false

    try {
      await axios.get('/users/verify-token')
      return true
    } catch (error: any) {
      console.error('Error verificando token:', error.response?.data)
      if (error.response?.data?.code === 'TOKEN_EXPIRED' || error.response?.data?.code === 'TOKEN_INVALID') {
        this.logout()
      }
      return false
    }
  }

  private async getIpAddress(): Promise<string> {
    try {
      const response = await axios.get('https://api.ipify.org?format=json')
      return response.data.ip
    } catch (error) {
      console.error('Error al obtener la IP:', error)
      return 'unknown'
    }
  }

  private saveSession(token: string, user: any): void {
    console.log('Guardando sesión:', { token, user })

    // Guardar token
    localStorage.setItem('user-token', token)

    // Preparar datos de usuario
    const userSession: UserSession = {
      id: user.id,
      username: user.username,
      company: user.company,
    }

    // Guardar en ambos storages
    const userSessionStr = JSON.stringify(userSession)
    localStorage.setItem('user-session', userSessionStr)
    sessionStorage.setItem('user-session', userSessionStr)

    // Iniciar verificación periódica
    this.startTokenVerification()

    console.log('Sesión guardada correctamente')
  }

  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const ipAddress = await this.getIpAddress()

      const loginData = {
        username,
        password,
        ip: ipAddress,
      }

      console.log('Enviando solicitud de login:', { username, ip: ipAddress })
      const response = await axios.post('/users/login', loginData)
      console.log('Respuesta del servidor:', response.data)

      if (response.data.token && response.data.user) {
        this.saveSession(response.data.token, response.data.user)
        return {
          token: response.data.token,
          user: response.data.user,
        }
      }

      throw new Error('Respuesta de login inválida')
    } catch (error: any) {
      console.error('Error en login:', error.response?.data || error)
      return {
        error: error.response?.data?.message || 'Error en el inicio de sesión',
      }
    }
  }

  logout(): void {
    this.stopTokenVerification()
    axios.post('/users/logout').catch(() => {
      // No debe bloquear el logout local si la llamada falla
    })
    localStorage.removeItem('user-token')
    localStorage.removeItem('user-session')
    sessionStorage.removeItem('user-session')
    window.location.href = '/auth/login'
  }

  getCurrentUser(): UserSession | null {
    try {
      let session = sessionStorage.getItem('user-session')
      if (!session) {
        session = localStorage.getItem('user-session')
      }
      return session ? JSON.parse(session) : null
    } catch (error) {
      console.error('Error al obtener usuario actual:', error)
      return null
    }
  }

  getToken(): string | null {
    const token = localStorage.getItem('user-token')
    return token
  }

  async isAuthenticated(): Promise<boolean> {
    const token = this.getToken()
    const user = this.getCurrentUser()

    if (!token || !user) {
      return false
    }

    // Verificar token con el backend
    return await this.verifyToken()
  }
}

export default new AuthService()
